import { getDay, parseISO } from 'date-fns'
import React, { CSSProperties, ReactNode, useCallback, useMemo, useState } from 'react'

import { IconChevronFilledUp, IconX } from '@avantstay/backoffice-vectors'

import { formatDate } from '../../utils'
import { Tooltip } from '../Tooltip'
import { CalendarLegend } from './CalendarLegend'
import { useYearCalendar } from './useYearCalendar'
import * as S from './YearCalendar.styles'

type YearCalendar<T> = {
  className?: string
  style?: CSSProperties
  title?: ReactNode
  year: number
  data: T[]
  dateFieldKey: keyof T
  dateFormat?: string
  getDayTooltip?: (date: string, cell?: T) => ReactNode
  dateSelected?: string | null
  onChangeDateSelected?: (dateSelected?: string) => void
  getDayColor?: (cell?: Partial<T>) => string | undefined
  renderDayContent?: (cell?: T) => ReactNode
  getWeekSummary?: (
    weekCells: T[],
    month: number,
  ) => { color: string; tooltip: ReactNode } | undefined
  legendItems?: Array<{ title: string; color: string }>
  renderMonthSummary?: (monthIndex: number) => ReactNode
  renderYearSummary?: () => ReactNode
  renderDateSelectedModal?: (cell: T, date: string, month: number) => ReactNode
}

/**
 * A customize year calendar component that displays a grid of months with day cells.
 *
 * When needed, appearance can be customized using CSS variables:
 * `--year-calendar-day-size`: sets the size (width/height) of individual day cells
 * `--year-calendar-month-gap`: sets the spacing between months grid
 */
export function YearCalendar<T>({
  className,
  style,
  title,
  year,
  data = [],
  dateFieldKey,
  dateFormat,
  getDayTooltip,
  dateSelected: _dateSelected,
  onChangeDateSelected,
  getDayColor,
  renderDayContent,
  getWeekSummary,
  legendItems,
  renderMonthSummary,
  renderYearSummary,
  renderDateSelectedModal,
}: YearCalendar<T>) {
  if (dateFormat && getDayTooltip) {
    throw new Error('YearCalendar: provide only one of dateFormat or getDayTooltip')
  }

  const [dateSelected, setDateSelected] = useState<string | null | undefined>(_dateSelected)
  const currentDateSelected = onChangeDateSelected ? _dateSelected : dateSelected
  const clickableCells = !!onChangeDateSelected || !!renderDateSelectedModal

  const { months, days } = useYearCalendar({ year, data, dateFieldKey, getDayColor })

  const isSameMonth = useCallback((dateA: string, dateB: string) => {
    return dateA.startsWith(dateB.slice(0, `yyyy-mm`.length))
  }, [])

  const handleCloseTooltip = useCallback(() => {
    setDateSelected(undefined)
    onChangeDateSelected?.(undefined)
  }, [onChangeDateSelected])

  const selectedDayIndex = useMemo(() => {
    if (!currentDateSelected) {
      return -1
    }
    const date = parseISO(currentDateSelected)
    const dayIndex = getDay(date)
    return dayIndex
  }, [currentDateSelected])

  const dateSelectedModalContent = useMemo(() => {
    if (!currentDateSelected || !renderDateSelectedModal) {
      return null
    }
    const day = days.find(day => day.date === currentDateSelected)
    if (!day) {
      return null
    }
    const [, month] = currentDateSelected.split('-')
    const dateSelectedContent = renderDateSelectedModal(day.data, day.date, parseInt(month, 10))
    return (
      <S.TooltipContainer>
        <S.ButtonCloseIcon onClick={handleCloseTooltip}>
          <IconX />
        </S.ButtonCloseIcon>
        {dateSelectedContent}
      </S.TooltipContainer>
    )
  }, [days, currentDateSelected, renderDateSelectedModal, handleCloseTooltip])

  const renderWeekSummary = useCallback(
    (weekCells: T[], month: number) => {
      if (!getWeekSummary) {
        return null
      }
      const summary = getWeekSummary(weekCells, month)
      const weekCell = <S.WeekCell $color={summary?.color} />
      return (
        <S.WeekSummary>
          {summary ? (
            <Tooltip offset={[0, 7]} placement="top" content={summary.tooltip}>
              {weekCell}
            </Tooltip>
          ) : (
            weekCell
          )}
        </S.WeekSummary>
      )
    },
    [getWeekSummary],
  )

  return (
    <S.Root className={className} style={style}>
      <S.CalendarTitle>{title ?? year}</S.CalendarTitle>
      <S.WrapperMonths>
        {months.map((month, monthIndex) => (
          <div key={`month-${month.monthName}`}>
            <S.MonthName>{month.monthName}</S.MonthName>
            {renderMonthSummary && (
              <S.SummaryMonthContainer>{renderMonthSummary(monthIndex)}</S.SummaryMonthContainer>
            )}
            {month.weeks.map((week, weekIndex) => (
              <S.Week key={`week-${weekIndex}`}>
                {week.map((day, dayIndex) => (
                  <React.Fragment key={`day-${day?.date ?? dayIndex}`}>
                    {day ? (
                      <Tooltip
                        offset={[0, 5]}
                        placement="top"
                        content={
                          getDayTooltip
                            ? getDayTooltip(day.date, day.data)
                            : dateFormat
                              ? formatDate(day.date, dateFormat)
                              : day.date
                        }
                      >
                        <S.Day
                          disabled={!clickableCells}
                          onClick={() => {
                            if (clickableCells) {
                              if (currentDateSelected === day.date) {
                                return handleCloseTooltip()
                              }
                              onChangeDateSelected?.(day.date)
                              setDateSelected(day.date)
                            }
                          }}
                        >
                          <S.DayCell
                            $color={day.color}
                            $selected={day.date === currentDateSelected}
                            disabled={!clickableCells}
                          >
                            {renderDayContent?.(day.data)}
                          </S.DayCell>
                        </S.Day>
                      </Tooltip>
                    ) : (
                      <S.DayEmpty />
                    )}
                  </React.Fragment>
                ))}

                {getWeekSummary &&
                  renderWeekSummary(
                    week.flatMap(w => (w?.data ? [w.data] : [])),
                    monthIndex,
                  )}
              </S.Week>
            ))}

            {renderDateSelectedModal &&
              currentDateSelected &&
              isSameMonth(currentDateSelected, month.monthDate) && (
                <S.SelectedMonthHighlight
                  dayIndex={selectedDayIndex}
                  hasWeekCell={!!getWeekSummary}
                >
                  <IconChevronFilledUp />
                </S.SelectedMonthHighlight>
              )}
          </div>
        ))}

        {renderYearSummary && (
          <S.YearSummaryContainer>{renderYearSummary()}</S.YearSummaryContainer>
        )}
      </S.WrapperMonths>

      {dateSelectedModalContent}

      {legendItems?.length && <CalendarLegend items={legendItems} />}
    </S.Root>
  )
}
